<script setup lang="ts">
import Breadcrumb from "../../Components/Breadcrumb.vue";
import VehicleCard from "@/Components/Cards/VehicleCard.vue";
import Pagination from "@/Components/Pagination.vue";
import {
    PaginatedResponse,
    VehicleAlgoliaRecord,
    VehicleMake,
} from "../../types";
import { useI18n } from "vue-i18n";
import { Head } from "@inertiajs/vue3";
import { inject, computed } from "vue";

const { t } = useI18n();
const route: any = inject("route");

const props = defineProps<{
    vehicleMake?: VehicleMake; // Optionnel : si absent = page globale
    soldVehicles: PaginatedResponse<VehicleAlgoliaRecord>;
    breadcrumbs: Array<{ label: string; path?: string }>;
    breadcrumbSchema?: string;
}>();

const metaTitle = computed(() => {
    return props.vehicleMake
        ? `Nos dernières ${props.vehicleMake.designation} vendues`
        : "Nos dernières ventes | Joinsteer";
});

const metaDescription = computed(() => {
    return props.vehicleMake
        ? `Découvrez les ${props.soldVehicles.total} dernières ${props.vehicleMake.designation} vendues sur le marché européen. De nouvelles opportunités arrivent chaque jour sur notre marketplace.`
        : `Découvrez les ${props.soldVehicles.total} derniers véhicules premium vendus sur le marché européen. De nouvelles opportunités arrivent chaque jour sur notre marketplace.`;
});

const canonicalUrl = computed(() => {
    if (!route) return "";
    return props.vehicleMake
        ? route("vehicleMake.sold", { vehicleMake: props.vehicleMake })
        : route("sold.index");
});
</script>

<template>
    <Head>
        <title>{{ metaTitle }}</title>
        <meta
            head-key="description"
            name="description"
            :content="metaDescription"
        />
        <link rel="canonical" :href="canonicalUrl" />
        <meta name="robots" content="noindex, follow" />
    </Head>

    <div class="container sold-vehicles-page">
        <Breadcrumb
            :items="breadcrumbs"
            :json-ld="breadcrumbSchema"
            :show-on-mobile="true"
        />

        <h1 class="sold-vehicles-page__title">
            <img
                v-if="vehicleMake?.logo_url"
                :src="vehicleMake.logo_url"
                :alt="vehicleMake.designation"
                class="sold-vehicles-page__title--logo"
            />
            {{
                vehicleMake
                    ? t("landingPage.soldTitle", {
                          make: vehicleMake.designation,
                      })
                    : t("soldVehicles.allTitle")
            }}
        </h1>

        <p class="sold-vehicles-page__subtitle">
            {{
                vehicleMake
                    ? t("landingPage.soldSubtitle", {
                          make: vehicleMake.designation,
                          count: soldVehicles.total,
                      })
                    : t("soldVehicles.allSubtitle", {
                          count: soldVehicles.total,
                      })
            }}
        </p>

        <ul
            v-if="soldVehicles.data.length > 0"
            class="sold-vehicles-page__grid"
        >
            <li v-for="vehicle in soldVehicles.data" :key="vehicle.id">
                <VehicleCard :item="vehicle" :is-sold="true" />
            </li>
        </ul>

        <div v-else class="sold-vehicles-page__empty">
            <p>Aucun véhicule vendu pour le moment.</p>
        </div>

        <Pagination :data="soldVehicles" />
    </div>
</template>

<style lang="scss" scoped>
.sold-vehicles-page {
    margin-top: 2.5rem;
    margin-bottom: 5rem;

    @include medium-up {
        margin-top: 5rem;
    }

    &__title {
        display: block;
        font-family: $bebas;
        line-height: 90%;
        font-weight: 700;
        font-size: 24px;
        color: #000000;
        text-transform: uppercase;
        margin: 1.5rem 0 0.75rem;

        @include medium-up {
            line-height: 46px;
            font-size: 52px;
        }

        &--logo {
            height: 1.5em;
            width: auto;
            margin-right: 0.5rem;
            vertical-align: middle;
        }
    }

    &__subtitle {
        color: rgba($dark-font, 0.7);
        font-size: 1rem;
        margin-bottom: 2rem;
        max-width: 800px;
    }

    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;
        list-style: none;
        padding: 0;

        @include medium-up {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    &__empty {
        text-align: center;
        padding: 3rem;
        background-color: #f2f5f6;
        border-radius: 4px;

        p {
            color: rgba($dark-font, 0.6);
            font-size: 1.1rem;
        }
    }
}
</style>
